<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  getHistoricalIndicators, getHistoricalShifts, getHistoricalTankSeries,
} from '../api.js'
import { useI18n } from '../i18n.js'
import NaftaPosInstanceSelector from '../components/NaftaPosInstanceSelector.vue'
import { chooseNaftaPosSource, session } from '../session'
import { activeNaftaPosInstance } from '../platform/context'
import IndicatorSummaryCards from '../components/indicators/IndicatorSummaryCards.vue'
import CashFlowPanel from '../components/indicators/CashFlowPanel.vue'
import HistoricalTankPanel from '../components/indicators/HistoricalTankPanel.vue'
import HistoricalCountersTable from '../components/indicators/HistoricalCountersTable.vue'

const activeStation = computed(() => activeNaftaPosInstance(session.platformContext))
const shifts = ref([])
const shiftsLoading = ref(true)
const loadingMoreShifts = ref(false)
const hasMoreShifts = ref(false)
const nextShiftCursor = ref('')
const selectedSession = ref(null)
const snapshot = ref(null)
const snapshotLoading = ref(false)
const selectedTankNumber = ref(null)
const tankSeries = ref(null)
const tankLoading = ref(false)
const switchingStation = ref(false)
const pageError = ref('')
const snapshotError = ref('')
const tankError = ref('')
const { t } = useI18n()

let stationEpoch = 0
let snapshotRequest = 0
let tankRequest = 0
let shiftListController = null
let snapshotController = null
let tankController = null
let tankCache = new Map()
const historyScope = () => JSON.stringify([session.user?.id ?? session.user?.username,
  session.user?.accessVersion, session.platformContext?.activeStationId,
  activeStation.value?.id, activeStation.value?.enabled])

const hasClosedShifts = computed(() => shifts.value.length > 0)
const busy = computed(() => switchingStation.value || shiftsLoading.value)

function shortDateTime(value) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('uk-UA', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(value))
}

function shiftLabel(shift) {
  return `№${shift.number} · ${shortDateTime(shift.startedAt)} — ${shortDateTime(shift.endedAt)}`
}

function sourceTime(value) {
  return value ? new Intl.DateTimeFormat('uk-UA', {
    dateStyle: 'short', timeStyle: 'medium',
  }).format(new Date(value)) : '—'
}

function isAbort(cause) {
  return cause?.name === 'AbortError'
}

function abortRequests() {
  shiftListController?.abort()
  snapshotController?.abort()
  tankController?.abort()
}

function resetHistory() {
  shifts.value = []
  hasMoreShifts.value = false
  nextShiftCursor.value = ''
  selectedSession.value = null
  snapshot.value = null
  snapshotError.value = ''
  selectedTankNumber.value = null
  tankSeries.value = null
  tankError.value = ''
  tankCache = new Map()
  loadingMoreShifts.value = false
  snapshotLoading.value = false
  tankLoading.value = false
}

async function loadInitial() {
  await loadStationHistory()
}

async function loadStationHistory() {
  const epoch = ++stationEpoch
  const stationId = activeStation.value?.enabled ? activeStation.value.id : null
  abortRequests()
  resetHistory()
  shiftsLoading.value = true
  pageError.value = ''
  if (!stationId) {
    shiftsLoading.value = false
    return
  }
  shiftListController = new AbortController()
  try {
    const result = await getHistoricalShifts(stationId, {
      limit: 50, signal: shiftListController.signal,
    })
    if (epoch !== stationEpoch || activeStation.value?.id !== stationId) return
    shifts.value = result.items ?? []
    hasMoreShifts.value = Boolean(result.hasMore)
    nextShiftCursor.value = result.nextCursor ?? ''
    const first = shifts.value[0]
    if (first) {
      selectedSession.value = first.number
      await loadSnapshot(first.number, epoch, stationId)
    }
  } catch (cause) {
    if (!isAbort(cause) && epoch === stationEpoch && activeStation.value?.id === stationId) {
      pageError.value = cause.message
    }
  } finally {
    if (epoch === stationEpoch && activeStation.value?.id === stationId) shiftsLoading.value = false
  }
}

async function loadOlderShifts() {
  if (!hasMoreShifts.value || !nextShiftCursor.value || loadingMoreShifts.value) return
  const epoch = stationEpoch
  const stationId = activeStation.value?.id
  if (!stationId) return
  loadingMoreShifts.value = true
  shiftListController?.abort()
  shiftListController = new AbortController()
  try {
    const result = await getHistoricalShifts(stationId, {
      cursor: nextShiftCursor.value, limit: 50, signal: shiftListController.signal,
    })
    if (epoch !== stationEpoch || activeStation.value?.id !== stationId) return
    const known = new Set(shifts.value.map((shift) => shift.number))
    shifts.value.push(...(result.items ?? []).filter((shift) => !known.has(shift.number)))
    hasMoreShifts.value = Boolean(result.hasMore)
    nextShiftCursor.value = result.nextCursor ?? ''
  } catch (cause) {
    if (!isAbort(cause) && epoch === stationEpoch && activeStation.value?.id === stationId) {
      pageError.value = cause.message
    }
  } finally {
    if (epoch === stationEpoch && activeStation.value?.id === stationId) {
      loadingMoreShifts.value = false
    }
  }
}

async function selectStation(profileId) {
  if (!profileId || activeStation.value?.id === profileId || switchingStation.value) return
  const scope = historyScope()
  switchingStation.value = true
  pageError.value = ''
  ++stationEpoch
  abortRequests()
  resetHistory()
  try {
    await chooseNaftaPosSource(profileId)
  } catch (cause) {
    if (!isAbort(cause) && scope === historyScope()) pageError.value = cause.message
  } finally {
    switchingStation.value = false
  }
}

async function selectShift(value) {
  const session = Number(value)
  if (!Number.isInteger(session) || session < 1 || session === selectedSession.value) return
  selectedSession.value = session
  await loadSnapshot(session, stationEpoch, activeStation.value?.id)
}

async function loadSnapshot(session = selectedSession.value, epoch = stationEpoch,
  stationId = activeStation.value?.id) {
  if (!session || !stationId) return
  const request = ++snapshotRequest
  snapshotController?.abort()
  tankController?.abort()
  snapshotController = new AbortController()
  snapshotLoading.value = true
  snapshotError.value = ''
  snapshot.value = null
  selectedTankNumber.value = null
  tankSeries.value = null
  tankError.value = ''
  tankCache = new Map()
  try {
    const result = await getHistoricalIndicators(stationId, session, {
      signal: snapshotController.signal,
    })
    if (request !== snapshotRequest || epoch !== stationEpoch
      || activeStation.value?.id !== stationId || session !== selectedSession.value) return
    snapshot.value = result
    const firstTank = result.tanks?.[0]
    if (firstTank) {
      selectedTankNumber.value = firstTank.tankNumber
      await loadTankSeries(firstTank.tankNumber, session, epoch, stationId)
    }
  } catch (cause) {
    if (!isAbort(cause) && request === snapshotRequest && epoch === stationEpoch
      && activeStation.value?.id === stationId) {
      snapshotError.value = cause.message
    }
  } finally {
    if (request === snapshotRequest && epoch === stationEpoch
      && activeStation.value?.id === stationId) snapshotLoading.value = false
  }
}

async function selectTank(tankNumber) {
  if (!Number.isInteger(tankNumber) || tankNumber < 1 || tankNumber === selectedTankNumber.value) return
  selectedTankNumber.value = tankNumber
  await loadTankSeries(tankNumber, selectedSession.value, stationEpoch, activeStation.value?.id)
}

async function loadTankSeries(tankNumber = selectedTankNumber.value,
  session = selectedSession.value, epoch = stationEpoch, stationId = activeStation.value?.id) {
  if (!tankNumber || !session || !stationId) return
  const cacheKey = `${stationId}:${session}:${tankNumber}`
  if (tankCache.has(cacheKey)) {
    tankSeries.value = tankCache.get(cacheKey)
    tankError.value = ''
    return
  }
  const request = ++tankRequest
  tankController?.abort()
  tankController = new AbortController()
  tankLoading.value = true
  tankError.value = ''
  tankSeries.value = null
  try {
    const result = await getHistoricalTankSeries(stationId, session, tankNumber, {
      signal: tankController.signal,
    })
    if (request !== tankRequest || epoch !== stationEpoch
      || activeStation.value?.id !== stationId || session !== selectedSession.value
      || tankNumber !== selectedTankNumber.value) return
    tankCache.set(cacheKey, result)
    tankSeries.value = result
  } catch (cause) {
    if (!isAbort(cause) && request === tankRequest && epoch === stationEpoch
      && activeStation.value?.id === stationId) {
      tankError.value = cause.message
    }
  } finally {
    if (request === tankRequest && epoch === stationEpoch
      && activeStation.value?.id === stationId) tankLoading.value = false
  }
}

async function refreshSelected() {
  if (!selectedSession.value) return loadStationHistory()
  await loadSnapshot(selectedSession.value, stationEpoch, activeStation.value?.id)
}

onMounted(loadInitial)
watch(historyScope, loadStationHistory, { flush: 'sync' })
onBeforeUnmount(() => {
  ++stationEpoch
  abortRequests()
})
</script>

<template>
  <section class="current-indicators-page historical-indicators-page">
    <div class="indicators-toolbar historical-indicators-toolbar">
      <div><p class="eyebrow">Онлайн показники</p><h1>Історія показників</h1><p v-if="snapshot" class="source-status" :class="`freshness-${snapshot.freshness}`">Отримано з АЗС: {{ sourceTime(snapshot.sampledAt) }}</p></div>
      <div class="indicator-actions historical-indicator-actions">
        <NaftaPosInstanceSelector :context="session.platformContext" :disabled="busy" @select="selectStation" />
        <label class="historical-shift-selector"><span class="visually-hidden">Закрита зміна</span><select :value="selectedSession ?? ''" :disabled="!hasClosedShifts || busy || snapshotLoading" @change="selectShift($event.target.value)"><option value="" disabled>Оберіть закриту зміну</option><option v-for="shift in shifts" :key="shift.number" :value="shift.number">{{ shiftLabel(shift) }}</option></select></label>
        <button v-if="hasMoreShifts" class="button secondary older-shifts-button" type="button" :disabled="loadingMoreShifts" @click="loadOlderShifts">{{ loadingMoreShifts ? 'Завантажуємо…' : 'Старіші зміни' }}</button>
        <button class="button refresh-button" type="button" :disabled="snapshotLoading || !activeStation || !selectedSession" @click="refreshSelected">{{ snapshotLoading ? 'Оновлюємо…' : 'Оновити' }}</button>
      </div>
    </div>

    <p v-if="!activeStation && !shiftsLoading" class="notice info">Оберіть робоче місце NaftaPOS цієї АЗС, щоб переглянути закриті зміни.</p>
    <p v-if="pageError" class="notice error" role="alert">{{ pageError }} <button class="inline-retry" type="button" @click="activeStation ? loadStationHistory() : loadInitial()">Спробувати ще раз</button></p>
    <div v-if="shiftsLoading" class="dashboard-loading" role="status"><p>Отримуємо список закритих змін з АЗС…</p></div>
    <p v-else-if="activeStation && !hasClosedShifts && !pageError" class="notice info">Для цієї АЗС закритих змін ще немає.</p>
    <div v-else-if="snapshotLoading" class="dashboard-loading" role="status"><p>Рахуємо показники вибраної зміни…</p></div>
    <p v-else-if="snapshotError" class="notice error" role="alert">{{ snapshotError }} <button class="inline-retry" type="button" @click="loadSnapshot()">Спробувати ще раз</button></p>
    <template v-else-if="snapshot">
      <div v-if="snapshot.warnings?.length" class="snapshot-warnings" role="status"><p v-for="warning in snapshot.warnings" :key="`${warning.section}-${warning.code}`">{{ warning.message }}</p></div>
      <IndicatorSummaryCards :shift="snapshot.shift" historical :empty-message="t('indicatorDataAbsent')" />
      <CashFlowPanel :cash-flow="snapshot.cashFlow" :empty-message="t('indicatorDataAbsent')" />
      <HistoricalTankPanel
        :tanks="snapshot.tanks"
        :selected-tank-number="selectedTankNumber"
        :series="tankSeries"
        :loading="tankLoading"
        :error="tankError"
        :empty-message="t('indicatorDataAbsent')"
        @select="selectTank"
        @retry="loadTankSeries()"
      />
      <HistoricalCountersTable :counters="snapshot.counters" :empty-message="t('indicatorDataAbsent')" />
    </template>
  </section>
</template>
